package org.moxultimate.topwithmox.components.cards

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.buttons.CardHoverStyle
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.theme.DarkGrayColor
import org.moxultimate.topwithmox.theme.DarkTextColor
import org.moxultimate.topwithmox.theme.FontFamily

/**
 * ContentCard
 * Reusable card used across Shows, Apps, Tech, and Products pages.
 * Structure: fixed-height cover image → type badge → title → description (3-line clamp)
 * → caller-supplied action buttons pinned to the card bottom.
 *
 * Cards in the same flex-wrap row reach equal height when the parent row uses
 * align-items:stretch (see each page's grid row).
 *
 * @param imageSrc Cover image URL; shows a gray placeholder when blank.
 * @param type Optional type badge rendered in gradient uppercase above the title.
 * @param title Card title shown in bold dark text.
 * @param description Optional body text clamped to 3 lines with trailing ellipsis.
 * @param modifier Outer modifier supplied by the parent grid (controls width + margin).
 * @param onDeleteClick When non-null, renders a circular delete icon button overlaid on the
 *   top-right corner of the cover image. Caller is responsible for any confirmation dialog.
 * @param actions Composable slot rendered as a Row at the card bottom (buttons go here).
 */
@Composable
fun ContentCard(
    imageSrc: String,
    type: String?,
    title: String,
    description: String?,
    modifier: Modifier = Modifier,
    onDeleteClick: (() -> Unit)? = null,
    actions: @Composable () -> Unit = {},
) {
    Column(
        modifier = CardHoverStyle.toModifier()
            .then(modifier)
            .fillMaxHeight()
            .backgroundColor(Colors.White)
            .borderRadius(16.px)
            .boxShadow(blurRadius = 20.px, color = Colors.LightGray)
            .overflow(Overflow.Hidden),
        horizontalAlignment = Alignment.Start,
    ) {
        // Fixed-height image — consistent across all cards regardless of source image dimensions
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(220.px)
                .styleModifier { property("position", "relative") },
        ) {
            if (imageSrc.isNotBlank()) {
                Image(
                    src = imageSrc,
                    description = title,
                    modifier = Modifier
                        .fillMaxWidth()
                        .fillMaxHeight()
                        .styleModifier {
                            property("object-fit", "cover")
                            property("display", "block")
                        }
                        .attrsModifier { attr("loading", "lazy") },
                )
            } else {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .fillMaxHeight()
                        .backgroundColor(Colors.LightGray),
                )
            }

            // Circular delete button — only rendered when caller supplies onDeleteClick
            if (onDeleteClick != null) {
                Box(
                    modifier = Modifier
                        .width(34.px)
                        .height(34.px)
                        .styleModifier {
                            property("position", "absolute")
                            property("top", "8px")
                            property("right", "8px")
                            property("border-radius", "50%")
                            property("background", "white")
                            property("box-shadow", "0 2px 8px rgba(0,0,0,0.28)")
                            property("cursor", "pointer")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("z-index", "10")
                            property("transition", "transform 0.15s, box-shadow 0.15s")
                        }
                        .onClick { onDeleteClick() },
                    contentAlignment = Alignment.Center,
                ) {
                    Image(
                        src = "/ic_delete.png",
                        description = "Delete",
                        modifier = Modifier
                            .width(18.px)
                            .height(18.px),
                    )
                }
            }
        }

        // Card body — fills remaining height; SpaceBetween pins actions to the bottom
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier { property("flex", "1") }
                .padding(all = 20.px),
            horizontalAlignment = Alignment.Start,
            verticalArrangement = Arrangement.SpaceBetween,
        ) {
            // Text content group: type badge + title + description
            Column(
                modifier = Modifier.fillMaxWidth(),
                horizontalAlignment = Alignment.Start,
            ) {
                if (!type.isNullOrBlank()) {
                    SpanText(
                        text = type.uppercase(),
                        modifier = Modifier
                            .fontFamily(FontFamily)
                            .fontSize(14.px)
                            .letterSpacing(0.15.cssRem)
                            .fontWeight(FontWeight.Bold)
                            .moxTextGradient(),
                    )
                }

                SpanText(
                    text = title,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 8.px)
                        .color(DarkTextColor)
                        .fontFamily(FontFamily)
                        .fontSize(clamp(min = 15.px, value = 1.1.vw, max = 19.px))
                        .fontWeight(FontWeight.Bold),
                )

                // Always rendered — min-height locks in 3-line space so all cards stay uniform
                SpanText(
                    text = description.orEmpty(),
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 10.px)
                        .color(DarkGrayColor)
                        .fontFamily(FontFamily)
                        .fontSize(13.px)
                        .fontWeight(FontWeight.Normal)
                        .styleModifier {
                            property("display", "-webkit-box")
                            property("-webkit-line-clamp", "3")
                            property("-webkit-box-orient", "vertical")
                            property("overflow", "hidden")
                            property("line-height", "1.6")
                            property("min-height", "calc(1.6em * 3)")
                        },
                )
            }

            // Actions pinned to bottom — caller provides button(s) via this slot
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = 16.px, bottom = 4.px),
                horizontalArrangement = Arrangement.spacedBy(10.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                actions()
            }
        }
    }
}
